// web/src/components/TopMenuBar.tsx
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Avatar from "./Avatar";
import { useViewport } from "../hooks/useViewport";
import { useSound } from "./SoundToggle";
import { useAchievements } from "./AchievementsProvider";
import { isAdsRemoved } from "../lib/unityAds";
import RemoveAdsModal from "./RemoveAdsModal";

const API_BASE = (import.meta as any).env?.VITE_API_BASE || "http://localhost:8100";

// Format Glint balance: drop decimals for >= 100, use notation for >= 10000
function formatGlint(glintBalance: string): string {
  const num = parseFloat(glintBalance);
  if (isNaN(num)) return "0.00";
  
  // For numbers >= 10000, use notation (10K, 100K, 1M, etc.)
  if (num >= 10000) {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
  }
  
  // For numbers >= 100, drop decimal places
  if (num >= 100) {
    return Math.floor(num).toString();
  }
  
  // For numbers < 100, keep 2 decimal places
  return num.toFixed(2);
}

export interface Account {
  id: number;
  username: string;
  email?: string;
  glint_balance: string;
  banked_glint?: string; // Banked glint balance (deposit only, no withdrawals)
  equipment_tier: string;
  equipment_charges: number;
  purchased_gear_charges?: number;
  purchased_gear_by_product?: { [productId: string]: number };
  tournament_won_gear?: number;
  email_verified: boolean;
  created_at: string;
  updated_at: string;
  role?: string; // 'user' or 'guest'
  unverified_games_played?: number; // Games played before email verification
  ads_removed?: boolean; // Remove ads purchase status
}

// Event to trigger login modal
declare global {
  interface WindowEventMap {
    showLoginModal: CustomEvent;
  }
}

interface TopMenuBarProps {
  account: Account;
  onLogout?: () => void;
}

// Audio icon button component - bigger size
function AudioIconButtonBigger({ scale = 1 }: { scale?: number }) {
  const { isMuted, toggleSound } = useSound();
  
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        toggleSound();
      }}
      className="hover:opacity-80 transition-opacity"
      style={{ 
        color: '#EAD2A3',
        padding: `${6 * scale}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      aria-label={isMuted ? 'Unmute sound' : 'Mute sound'}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-7 w-7"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
        style={{ width: `${28 * scale}px`, height: `${28 * scale}px` }}
      >
        {isMuted ? (
          // Muted icon - speaker with X
          <>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
          </>
        ) : (
          // Unmuted icon - speaker
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
        )}
      </svg>
    </button>
  );
}

export default function TopMenuBar({ account, onLogout }: TopMenuBarProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Array<{ id: number; name: string; tier?: string; scheduledStartTime: string; status: string; matchId?: number; cancellationReason?: string }>>([]);
  const { hasNew: hasAchievementNew, recentNew, refresh: refreshAchievements } = useAchievements();
  const [adsRemovedLocal, setAdsRemovedLocal] = useState(isAdsRemoved());
  const [cancellationTimestamps, setCancellationTimestamps] = useState<Map<number, number>>(new Map());
  const [previousTournamentStates, setPreviousTournamentStates] = useState<Map<number, { status: string; entryStatus?: string }>>(new Map());
  const cancellationTimestampsRef = useRef<Map<number, number>>(new Map());
  const previousTournamentStatesRef = useRef<Map<number, { status: string; entryStatus?: string }>>(new Map());
  const refreshedForCancelledTournamentsRef = useRef<Set<number>>(new Set());
  const notificationsRef = useRef<HTMLDivElement>(null);
  const menuScrollRef = useRef<HTMLDivElement>(null);
  const menuScrollRefPortrait = useRef<HTMLDivElement>(null);
  const [showTopIndicator, setShowTopIndicator] = useState(false);
  const [showBottomIndicator, setShowBottomIndicator] = useState(true);
  const [showTopIndicatorPortrait, setShowTopIndicatorPortrait] = useState(false);
  const [showBottomIndicatorPortrait, setShowBottomIndicatorPortrait] = useState(true);
  const [showRemoveAdsModal, setShowRemoveAdsModal] = useState(false);
  
  // Keep refs in sync with state
  useEffect(() => {
    cancellationTimestampsRef.current = cancellationTimestamps;
  }, [cancellationTimestamps]);
  
  useEffect(() => {
    previousTournamentStatesRef.current = previousTournamentStates;
  }, [previousTournamentStates]);

  useEffect(() => {
    void refreshAchievements();
  }, [refreshAchievements, account.id]);

  useEffect(() => {
    setAdsRemovedLocal(isAdsRemoved());
  }, [account.id]);

  useEffect(() => {
    const handleAdsRemoved = () => {
      setAdsRemovedLocal(isAdsRemoved());
    };
    window.addEventListener("adsRemovedChanged", handleAdsRemoved as EventListener);
    return () => window.removeEventListener("adsRemovedChanged", handleAdsRemoved as EventListener);
  }, []);

  // Handle scroll detection for mobile landscape menu
  const handleMenuScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const isAtTop = target.scrollTop === 0;
    const isAtBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 1; // +1 for rounding
    
    setShowTopIndicator(!isAtTop);
    setShowBottomIndicator(!isAtBottom);
  };

  // Handle scroll detection for portrait menu
  const handleMenuScrollPortrait = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const isAtTop = target.scrollTop === 0;
    const isAtBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 1; // +1 for rounding
    
    setShowTopIndicatorPortrait(!isAtTop);
    setShowBottomIndicatorPortrait(!isAtBottom);
  };

  // Check scroll position when menu opens
  useEffect(() => {
    if (isMenuOpen && menuScrollRef.current) {
      const target = menuScrollRef.current;
      const isAtTop = target.scrollTop === 0;
      const isAtBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 1;
      
      setShowTopIndicator(!isAtTop);
      setShowBottomIndicator(!isAtBottom);
    } else {
      setShowTopIndicator(false);
      setShowBottomIndicator(true);
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (isMenuOpen && menuScrollRefPortrait.current) {
      const target = menuScrollRefPortrait.current;
      const isAtTop = target.scrollTop === 0;
      const isAtBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 1;
      
      setShowTopIndicatorPortrait(!isAtTop);
      setShowBottomIndicatorPortrait(!isAtBottom);
    } else {
      setShowTopIndicatorPortrait(false);
      setShowBottomIndicatorPortrait(true);
    }
  }, [isMenuOpen]);

  const navigate = useNavigate();
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isPortrait = viewport.isPortrait && viewport.width < 1024;
  const isMobileLandscape = !viewport.isPortrait && viewport.isMobile;
  // Narrow landscape (mobile/tablet) where we want to tuck Glint/Gear under the username
  const isCompactLandscape = !isPortrait && viewport.width < 768;
  // Scale factor for Android tablets - slightly less than menu background (12% vs 15%)
  const androidTabletTextScale = viewport.isAndroidTablet && isMobileLandscape ? 1.12 : 1;
  const userAgent = typeof navigator !== 'undefined'
    ? (navigator.userAgent || navigator.vendor || (window as any).opera || '').toLowerCase()
    : '';
  const platform = typeof navigator !== 'undefined' ? (((navigator as any).platform || '') as string).toLowerCase() : '';
  const maxTouchPoints = typeof navigator !== 'undefined' ? (navigator as any).maxTouchPoints || 0 : 0;
  const isIOSApp = typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform && (window as any).Capacitor.getPlatform() === 'ios';
  const isIOSDevice = /ipad|iphone|ipod/.test(userAgent);
  const isIOS = isIOSDevice || isIOSApp;
  const isIOSPad = (() => {
    if (!isIOS) return false;
    if (userAgent.includes('ipad')) return true;
    if (platform === 'macintel' && maxTouchPoints > 1) {
      const maxDim = Math.max(viewport.width, viewport.height);
      const minDim = Math.min(viewport.width, viewport.height);
      if (maxDim >= 768 && minDim >= 768) return true;
    }
    const maxDim = Math.max(viewport.width, viewport.height);
    const minDim = Math.min(viewport.width, viewport.height);
    if (maxDim >= 768 && minDim >= 768 && isIOS) return true;
    return false;
  })();
  const isIPhone = (() => {
    if (!isIOS) return false;
    if (isIOSPad) return false;
    const maxDim = Math.max(viewport.width, viewport.height);
    const minDim = Math.min(viewport.width, viewport.height);
    if (userAgent.includes('iphone') && !userAgent.includes('ipad')) return true;
    if (userAgent.includes('ipod')) return true;
    if (isIOSApp && !isIOSPad) return true;
    if (isIOSDevice && !isIOSPad && (maxDim < 768 || minDim < 768)) return true;
    return false;
  })();
  const iosPadScale = isIOSPad && isMobileLandscape ? 1.2 : 1;
  const landscapeMenuScale = isIOSPad ? (isMobileLandscape ? Math.max(androidTabletTextScale, iosPadScale) : 1) : androidTabletTextScale;
  const menuMaxHeight = 'calc(100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 20px)';
  const portraitNotificationTop = isIOSPad ? "calc(env(safe-area-inset-top, 0px) + 160px)" : "calc(env(safe-area-inset-top, 0px) + 48px)";
  const landscapeMenuFixedStyle = isIOSPad && isMobileLandscape ? {
    position: 'fixed' as const,
    top: 'calc(env(safe-area-inset-top, 0px) + 14px)',
    left: 'calc(env(safe-area-inset-left, 0px) + 96px)',
    transform: 'translateY(0)',
  } : {};
  const hasNotificationDot = notifications.length > 0 || hasAchievementNew;
  const hasAnyNotifications = notifications.length > 0 || recentNew.length > 0;

  const renderNotificationsContent = () => {
    if (!hasAnyNotifications) {
      return (
        <div className="px-4 py-6 text-center text-sm" style={{ color: '#3e2615' }}>
          No notifications yet
        </div>
      );
    }

    return (
      <>
        {recentNew.map((achievement, idx) => {
          const showBorder = !(idx === recentNew.length - 1 && notifications.length === 0);
          return (
            <div
              key={`achievement-${achievement.achievement_id}-${achievement.earned_at}`}
              className="px-4 py-3 border-b"
              style={{
                borderColor: '#8b6542',
                borderBottom: showBorder ? undefined : 'none',
              }}
            >
              <p className="text-sm mb-2" style={{ color: '#3e2615' }}>
                Achievement unlocked: <strong>{achievement.name}</strong>
              </p>
              <button
                onClick={() => {
                  setNotificationsOpen(false);
                  navigate('/achievements');
                }}
                className="w-full px-3 py-2 rounded-lg text-sm font-semibold transition-all hover:scale-105"
                style={{
                  background: '#8b6542',
                  color: '#fffaeb',
                  border: '2px solid #8b6542',
                }}
              >
                View Achievements
              </button>
            </div>
          );
        })}
        {notifications.map((notif, idx) => {
          const showBorder = idx !== notifications.length - 1;
          return (
            <div
              key={notif.id}
              className="px-4 py-3 border-b"
              style={{
                borderColor: '#8b6542',
                borderBottom: showBorder ? undefined : 'none',
              }}
            >
              {notif.status === "cancelled" ? (
                <>
                  <p className="text-sm mb-2" style={{ color: '#3e2615' }}>
                    Your <strong>{formatTournamentTierForNotification(notif.tier)}</strong> was cancelled due to insufficient players. Your gear has been refunded.
                  </p>
                  <button
                    onClick={() => {
                      setNotificationsOpen(false);
                      navigate('/tournaments');
                    }}
                    className="w-full px-3 py-2 rounded-lg text-sm font-semibold transition-all hover:scale-105"
                    style={{
                      background: '#8b6542',
                      color: '#fffaeb',
                      border: '2px solid #8b6542',
                    }}
                  >
                    View Tournaments
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm mb-2" style={{ color: '#3e2615' }}>
                    Your <strong>{formatTournamentTierForNotification(notif.tier)}</strong> is starting soon.
                  </p>
                  <button
                    onClick={() => handlePlayMatch(notif.id, notif.matchId)}
                    className="w-full px-3 py-2 rounded-lg text-sm font-semibold transition-all hover:scale-105"
                    style={{
                      background: '#8b6542',
                      color: '#fffaeb',
                      border: '2px solid #8b6542',
                    }}
                  >
                    Play Match
                  </button>
                </>
              )}
            </div>
          );
        })}
      </>
    );
  };

  const formatTournamentTierForNotification = (tier?: string): string => {
    if (!tier) return "Tournament";
    const tierMap: { [key: string]: string } = {
      "daily": "Daily Tournament",
      "weekly": "Weekly Tournament",
      "monthly": "Monthly Tournament",
      "high_roller": "High Roller Tournament",
      "season_finale": "Season Finale Tournament",
    };
    return tierMap[tier] || `${tier.charAt(0).toUpperCase() + tier.slice(1)} Tournament`;
  };

  // Close menu when clicking outside
  useEffect(() => {
    if (!isMenuOpen) return;
    const handleClickOutside = () => setIsMenuOpen(false);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMenuOpen]);

  // Close notifications dropdown when clicking outside
  useEffect(() => {
    if (!notificationsOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (notificationsRef.current && !notificationsRef.current.contains(e.target as Node)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notificationsOpen]);

  // Check for upcoming tournaments
  useEffect(() => {
    // Capture refs in closure to avoid stale values
    const timestampsRef = cancellationTimestampsRef;
    const statesRef = previousTournamentStatesRef;
    
    const checkTournaments = async () => {
      try {
        // Check for regular user JWT token first (all platforms - JWT is primary)
        const headers: HeadersInit = { "Content-Type": "application/json" };
        let regularUserToken: string | null = null;
        if (typeof window !== 'undefined') {
          regularUserToken = localStorage.getItem('regular_user_jwt');
        }
        if (regularUserToken) {
          headers["Authorization"] = `Bearer ${regularUserToken}`;
        } else {
          // Fallback to guest token
          try {
            const { getValidGuestToken } = await import('../lib/guest-auth');
            const guestToken = await getValidGuestToken();
            if (guestToken) {
              headers["Authorization"] = `Bearer ${guestToken}`;
            }
          } catch {
            // Continue without token
          }
        }

        const response = await fetch(`${API_BASE}/tournaments?category=active`, {
          credentials: "include",
          headers,
        });

        if (!response.ok) {
          return;
        }

        const data = await response.json();
        const activeTournaments: any[] = data.tournaments || [];

        const now = Date.now();
        const WARNING_THRESHOLD_MS = 90 * 1000; // 90 seconds
        const CANCELLED_NOTIFICATION_MS = 5 * 60 * 1000; // 5 minutes

        // Detect cancelled tournaments and track cancellation timestamps
        // Use refs to get current values (not stale closure values)
        const currentStates = new Map<number, { status: string; entryStatus?: string }>();
        let updatedTimestamps = new Map(timestampsRef.current);
        const prevStates = statesRef.current;
        
        for (const t of activeTournaments) {
          if (t.user_entry) {
            currentStates.set(t.id, { status: t.status, entryStatus: t.user_entry.status });
            
            if (t.status === "cancelled") {
              // Check if tournament just became cancelled (transition detection)
              const prevState = prevStates.get(t.id);
              if (prevState && prevState.status !== "cancelled") {
                // Tournament just became cancelled - record timestamp
                if (!updatedTimestamps.has(t.id)) {
                  updatedTimestamps.set(t.id, now);
                }
              } else if (!updatedTimestamps.has(t.id)) {
                // Tournament is cancelled but we don't have a timestamp yet
                // Use registration_close_time as proxy for cancellation time
                // (tournaments are cancelled when registration closes if insufficient players)
                if (t.registration_close_time) {
                  const regCloseTime = new Date(t.registration_close_time).getTime();
                  const timeSinceCancellation = now - regCloseTime;
                  // If cancelled within the last 5 minutes, set timestamp
                  if (timeSinceCancellation >= 0 && timeSinceCancellation <= CANCELLED_NOTIFICATION_MS) {
                    updatedTimestamps.set(t.id, regCloseTime);
                  }
                }
              }
              // Refresh account data to show refunded gear if we haven't refreshed for this tournament yet
              // This ensures gear totals update when a tournament is cancelled, regardless of when we detect it
              if (!refreshedForCancelledTournamentsRef.current.has(t.id)) {
                refreshedForCancelledTournamentsRef.current.add(t.id);
                window.dispatchEvent(new CustomEvent('accountNeedsRefresh'));
              }
            }
          }
        }
        
        setPreviousTournamentStates(currentStates);
        
        // Remove old cancellation timestamps (older than 5 minutes)
        for (const [id, timestamp] of updatedTimestamps.entries()) {
          if (now - timestamp > CANCELLED_NOTIFICATION_MS) {
            updatedTimestamps.delete(id);
          }
        }
        
        setCancellationTimestamps(updatedTimestamps);

        // Find cancelled tournaments that should show notifications
        const cancelledTournaments = activeTournaments
          .filter((t: any) => {
            if (!t.user_entry) return false;
            if (t.status !== "cancelled") return false;
            const cancellationTime = updatedTimestamps.get(t.id);
            // Show notification if cancelled within the last 5 minutes
            return cancellationTime && (now - cancellationTime <= CANCELLED_NOTIFICATION_MS);
          })
          .map((t: any) => ({
            id: t.id,
            name: t.name,
            tier: t.tier,
            scheduledStartTime: t.scheduled_start_time || "",
            status: "cancelled",
            cancellationReason: t.cancellation_reason,
          }));

        // Find tournaments where user has an entry
        // Include tournaments that are starting soon OR already in progress
        // Exclude tournaments where user is eliminated or disqualified
        // Exclude cancelled tournaments (handled separately above)
        const soonTournaments = activeTournaments
          .filter((t: any) => {
            if (!t.user_entry || !t.scheduled_start_time) return false;
            if (t.status === "cancelled") return false; // Exclude cancelled (handled separately)
            const entryStatus = t.user_entry.status;
            if (entryStatus === "eliminated" || entryStatus === "disqualified") return false;
            return true;
          })
          .map((t: any) => {
            const startMs = new Date(t.scheduled_start_time).getTime();
            const timeUntilStart = startMs - now;
            const isInProgress = t.status === "in_progress";
            
            return {
              ...t,
              startMs,
              timeUntilStart,
              isInProgress,
            };
          })
          .filter((t: any) => {
            // Include if:
            // 1. Tournament is in progress (already started) - user can still play matches
            // 2. Tournament starts within the warning threshold (not started yet)
            return t.isInProgress || (t.timeUntilStart > 0 && t.timeUntilStart <= WARNING_THRESHOLD_MS);
          })
          .sort((a: any, b: any) => {
            if (a.isInProgress !== b.isInProgress) {
              return a.isInProgress ? -1 : 1;
            }
            return a.startMs - b.startMs;
          });

        // Fetch match info for in-progress tournaments
        const notificationPromises = soonTournaments.map(async (t: any) => {
          let matchId: number | undefined;
          if (t.isInProgress) {
            try {
              const matchHeaders: HeadersInit = { "Content-Type": "application/json" };
              try {
                const { getValidGuestToken } = await import('../lib/guest-auth');
                const guestToken = await getValidGuestToken();
                if (guestToken) {
                  matchHeaders["Authorization"] = `Bearer ${guestToken}`;
                }
              } catch {}
              
              const matchResponse = await fetch(`${API_BASE}/tournaments/${t.id}/my-match`, {
                credentials: "include",
                headers: matchHeaders,
              });
              if (matchResponse.ok) {
                const matchData = await matchResponse.json();
                matchId = matchData.match?.id;
              }
            } catch (err) {
              console.error(`[Notifications] Error fetching match for tournament ${t.id}:`, err);
            }
          }
          
          return {
            id: t.id,
            name: t.name,
            tier: t.tier,
            scheduledStartTime: t.scheduled_start_time,
            status: t.status,
            matchId,
          };
        });

        const regularNotifications = await Promise.all(notificationPromises);
        
        // Combine cancelled and regular notifications, prioritizing cancelled
        const notificationList = [...cancelledTournaments, ...regularNotifications];
        setNotifications(notificationList);
      } catch (error) {
        console.error('[Notifications] Error checking tournaments:', error);
      }
    };

    checkTournaments();
    const interval = setInterval(checkTournaments, 5000); // Check every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const handlePlayMatch = async (tournamentId: number, matchId?: number) => {
    if (matchId) {
      // Tournament has started, try to load the game directly
      try {
        const headers: HeadersInit = { "Content-Type": "application/json" };
        try {
          const { getValidGuestToken } = await import('../lib/guest-auth');
          const guestToken = await getValidGuestToken();
          if (guestToken) {
            headers["Authorization"] = `Bearer ${guestToken}`;
          }
        } catch {}

        const matchResponse = await fetch(`${API_BASE}/tournaments/${tournamentId}/matches/${matchId}`, {
          credentials: "include",
          headers,
        });

        if (matchResponse.ok) {
          const matchData = await matchResponse.json();
          navigate(`/play?tournament=${tournamentId}&match=${matchId}&opponent=${encodeURIComponent(matchData.opponent_username || 'Opponent')}`);
          setNotificationsOpen(false);
          return;
        }
      } catch (err) {
        console.error('[Notifications] Error loading match:', err);
      }
    }
    
    // Fallback: navigate to tournaments page
    navigate('/tournaments');
    setNotificationsOpen(false);
  };

  const totalGear = (() => {
    const freeGear = account.equipment_charges || 0;
    let purchasedGear = 0;
    
    if (account.purchased_gear_by_product && typeof account.purchased_gear_by_product === 'object') {
      try {
        const values = Object.values(account.purchased_gear_by_product);
        purchasedGear = values.reduce((sum: number, count: any) => {
          const num = typeof count === 'number' ? count : parseInt(String(count), 10) || 0;
          return sum + num;
        }, 0);
      } catch (e) {
        console.warn('Error calculating purchased gear from by_product:', e);
        purchasedGear = account.purchased_gear_charges || 0;
      }
    } else {
      purchasedGear = account.purchased_gear_charges || 0;
    }
    
    const tournamentWonGear = account.tournament_won_gear || 0;
    
    return freeGear + purchasedGear + tournamentWonGear;
  })();

  return (
    <div 
      className={`${isMobileLandscape ? 'w-full rounded-lg flex-col items-center justify-start overflow-visible' : 'w-full rounded-lg flex items-center'} gap-2 md:gap-4 relative ${isMobileLandscape ? 'px-2 py-2' : 'px-3 py-1.5 md:px-4 md:py-3'}`}
      style={{
        background: '#3A2A1E',
        border: '1px solid #3e2615',
        ...(isMobileLandscape && { 
          borderTop: 'none',
          paddingTop: '8px',
          paddingBottom: '0px',
        }),
      }}
    >
      {/* Mobile Landscape: Vertical layout */}
      {isMobileLandscape ? (
        <>
          {/* Gold cap section on top - transparent background, subdued text, moved up by text height */}
          <div 
            className="w-full flex flex-col items-center justify-center flex-shrink-0"
            style={{
              background: 'transparent',
              padding: '2px 4px',
              paddingTop: '10px', // Add padding above GOLDRUSH text
              marginTop: '-12px', // Move up by text-xs height (~12px)
              marginLeft: '-2px',
              marginRight: '-2px',
              width: 'calc(100% + 4px)',
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
              position: 'relative',
              zIndex: 1
            }}
          >
            <Link
              to="/"
              className="hover:opacity-80 transition-opacity w-full flex items-center justify-center"
              style={{ textDecoration: 'none' }}
            >
              <span className="text-[10px] font-medium opacity-70" style={{ 
                color: '#c1804c', 
                letterSpacing: '0.5px',
                fontSize: `${10 * (isIOSPad ? landscapeMenuScale : androidTabletTextScale)}px`
              }}>
                GOLDRUSH
              </span>
            </Link>
          </div>

          {/* Divider after goldrush - right under goldrush text, closer to text than avatar */}
          <div className="w-full h-px mt-0 mb-2" style={{ background: '#8b6542' }} />

          {/* Avatar and Username - bigger avatar, no double border */}
            <div className="flex flex-col items-center gap-1 flex-shrink-0 mb-1 px-3 mt-1">
            <Link
              to="/account"
              className="hover:opacity-80 transition-opacity"
              style={{ textDecoration: 'none', display: 'block' }}
            >
              <div style={{ width: `${48 * (isIOSPad ? landscapeMenuScale : androidTabletTextScale)}px`, height: `${48 * (isIOSPad ? landscapeMenuScale : androidTabletTextScale)}px`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Avatar name={account.username} size={48 * (isIOSPad ? landscapeMenuScale : androidTabletTextScale)} className="border-0" />
              </div>
            </Link>
            <Link
              to="/account"
              className="hover:opacity-80 transition-opacity"
              style={{ textDecoration: 'none' }}
            >
              <span 
                className="font-medium underline text-xs text-center block truncate max-w-[80px]" 
                style={{ 
                  color: '#F6E9D6', 
                  padding: '2px 4px',
                  fontSize: `${12 * (isIOSPad ? landscapeMenuScale : androidTabletTextScale)}px` // text-xs = 12px
                }}
                title={account.username}
              >
                {account.username}
              </span>
            </Link>
          </div>

          {/* Divider before glint/gear - moved down */}
          <div className="w-full h-px" style={{ background: '#8b6542', marginTop: '18px', marginBottom: '0.125rem' }} />

          {/* Glint and Gear - centered on menubar, left-justified so icons stack perfectly on top of each other */}
          <div className="flex flex-col items-center justify-center gap-1.5 flex-shrink-0 w-full" style={{ minHeight: 'fit-content', paddingTop: '0.125rem', paddingBottom: '0.375rem' }}>
            <div className="flex items-center justify-center" style={{ width: 'fit-content' }}>
              <span className="text-sm whitespace-nowrap flex items-center" style={{ color: '#F6E9D6', fontSize: `${14 * (isIOSPad ? landscapeMenuScale : androidTabletTextScale)}px` }}>
                <span style={{ fontSize: `${((isIPhone || isIOSPad) ? 32 : 16) * (isIOSPad ? landscapeMenuScale : androidTabletTextScale)}px`, width: `${((isIPhone || isIOSPad) ? 36 : 20) * (isIOSPad ? landscapeMenuScale : androidTabletTextScale)}px`, display: 'inline-block', textAlign: 'left', flexShrink: 0, lineHeight: '1', paddingLeft: '0', marginLeft: '0', paddingRight: (isIPhone || isIOSPad) ? '0' : undefined, marginRight: (isIPhone || isIOSPad) ? '-7px' : undefined }}>✧</span>
                <span style={{ marginLeft: `${((isIPhone || isIOSPad) ? -4 : 8) * (isIOSPad ? landscapeMenuScale : androidTabletTextScale)}px`, display: 'inline-block', textAlign: 'left' }}>{formatGlint(account.glint_balance)}</span>
              </span>
            </div>
            <div className="flex items-center justify-center" style={{ width: 'fit-content' }}>
              <span className="text-sm whitespace-nowrap flex items-center" style={{ color: '#F6E9D6', fontSize: `${14 * (isIOSPad ? landscapeMenuScale : androidTabletTextScale)}px` }}>
                <span style={{ fontSize: `${16 * (isIOSPad ? landscapeMenuScale : androidTabletTextScale)}px`, width: `${20 * (isIOSPad ? landscapeMenuScale : androidTabletTextScale)}px`, display: 'inline-block', textAlign: 'left', flexShrink: 0, lineHeight: '1', paddingLeft: '0', marginLeft: '0' }}>⚙️</span>
                <span style={{ marginLeft: `${8 * (isIOSPad ? landscapeMenuScale : androidTabletTextScale)}px`, display: 'inline-block', textAlign: 'left' }}>{totalGear > 0 ? totalGear : '0'}</span>
              </span>
            </div>
          </div>

          {/* Divider after glint/gear */}
          <div className="w-full h-px" style={{ background: '#8b6542', marginTop: '0.25rem', marginBottom: '0.25rem' }} />

          {/* Notifications, Audio, and Hamburger - INSIDE menu bar, reordered and sized */}
          <div className="flex-1 w-full flex flex-col items-center justify-between pt-1" style={{ paddingBottom: '0', minHeight: '0' }}>
            {/* Bell + Audio icons side by side - moved in towards center, very close together */}
            <div className="flex flex-row items-center justify-center gap-0 w-full px-6 mt-2 mb-2" style={{ marginTop: 'calc(0.5rem - 4px)' }}>
              {/* Notifications */}
              <div className="relative flex items-center justify-center" ref={notificationsRef}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setNotificationsOpen(!notificationsOpen);
                  }}
                  className="hover:opacity-80 transition-opacity relative"
                  style={{
                    color: hasNotificationDot ? '#fee067' : '#EAD2A3',
                    padding: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  aria-label="Notifications"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-7 w-7"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    style={{ width: `${28 * (isIOSPad ? landscapeMenuScale : androidTabletTextScale)}px`, height: `${28 * (isIOSPad ? landscapeMenuScale : androidTabletTextScale)}px` }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {hasNotificationDot && (
                    <span
                      className="absolute top-1 right-1 w-2 h-2 rounded-full"
                      style={{ background: '#fee067' }}
                    />
                  )}
                </button>
                {notificationsOpen && (
                  isIOSPad && isMobileLandscape ? (
                    <div
                      className="absolute left-full top-full ml-2 rounded-lg border-2 shadow-lg"
                      style={{
                        background: '#fffaeb',
                        borderColor: '#8b6542',
                        minWidth: '280px',
                        maxWidth: '320px',
                        maxHeight: 'min(70vh, 320px)',
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        zIndex: 9999,
                        transform: 'translate(40px, -20px)'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {renderNotificationsContent()}
                    </div>
                  ) : (
                    <div
                      className={`absolute left-full ${isIPhone ? 'top-1/2' : 'bottom-0'} ml-2 rounded-lg border-2 shadow-lg`}
                      style={{
                        background: '#fffaeb',
                        borderColor: '#8b6542',
                        minWidth: '280px',
                        maxWidth: '320px',
                        maxHeight: 'min(70vh, 320px)',
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        zIndex: 9999,
                        ...(isIPhone ? { transform: 'translate(28px, -100%)' } : {})
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {renderNotificationsContent()}
                    </div>
                  )
                )}
              </div>

              {/* Audio icon */}
              <AudioIconButtonBigger scale={landscapeMenuScale} />
            </div>

            {/* Spacing between bell/audio group and hamburger */}
            <div className="flex-1" style={{ minHeight: '8px' }} />

            {/* Big Hamburger button */}
            <div className="relative flex-shrink-0 w-full flex justify-center" style={{ marginTop: '-28px', paddingBottom: '0px', marginBottom: '-10px' }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMenuOpen(!isMenuOpen);
                }}
                className="hover:opacity-80 transition-opacity"
                style={{
                  color: '#EAD2A3',
                  padding: '14px 8px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
                aria-label="Menu"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-11 w-11"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.6}
                style={{ width: `${44 * (isIOSPad ? landscapeMenuScale : androidTabletTextScale)}px`, height: `${44 * (isIOSPad ? landscapeMenuScale : androidTabletTextScale)}px` }}
                >
                  {isMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
              {isMenuOpen && (
                <div
                  className="absolute left-full bottom-0 ml-2 rounded-lg border-2 shadow-lg"
                  style={isIOSPad && isMobileLandscape ? {
                    background: '#fffaeb',
                    borderColor: '#8b6542',
                    minWidth: '180px',
                    maxHeight: menuMaxHeight,
                    zIndex: 9999,
                    display: 'flex',
                    flexDirection: 'column',
                    paddingTop: `${4 * landscapeMenuScale}px`,
                    paddingBottom: `${4 * landscapeMenuScale}px`,
                  } : {
                    background: '#fffaeb',
                    borderColor: '#8b6542',
                    minWidth: '180px',
                    maxHeight: '80vh',
                    zIndex: 9999,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                {/* Top scroll indicator - triangle pointing up */}
                {showTopIndicator && (
                  <div className="flex justify-center py-1 rounded-t-lg" style={{ borderBottom: '1px solid #8b6542', background: '#fffaeb', flexShrink: 0 }}>
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 7L6 2L11 7" stroke="#8b6542" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
                <div 
                  ref={menuScrollRef}
                  onScroll={handleMenuScroll}
                  style={{ overflowY: 'auto', flex: 1 }}
                >
                  <Link
                    to="/play"
                    className="block px-4 py-3 text-sm font-semibold hover:bg-[#f4a261] transition-colors"
                    style={{ 
                      color: '#3e2615', 
                      borderRadius: showTopIndicator ? '0' : '0.5rem 0.5rem 0 0',
                      fontSize: `${14 * androidTabletTextScale}px`,
                      padding: `${12 * androidTabletTextScale}px ${16 * androidTabletTextScale}px`
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Enter Mine
                  </Link>
                  <div className="h-px" style={{ background: '#8b6542' }} />
                  <Link
                    to="/store"
                    className="block px-4 py-3 text-sm font-semibold hover:bg-[#f4a261] transition-colors"
                    style={{ 
                      color: '#3e2615',
                      fontSize: `${14 * androidTabletTextScale}px`,
                      padding: `${12 * androidTabletTextScale}px ${16 * androidTabletTextScale}px`
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Store
                  </Link>
                  <div className="h-px" style={{ background: '#8b6542' }} />
                  <Link
                    to="/leaderboard"
                    className="block px-4 py-3 text-sm font-semibold hover:bg-[#f4a261] transition-colors"
                    style={{ 
                      color: '#3e2615',
                      fontSize: `${14 * androidTabletTextScale}px`,
                      padding: `${12 * androidTabletTextScale}px ${16 * androidTabletTextScale}px`
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Leaderboard
                  </Link>
                  <div className="h-px" style={{ background: '#8b6542' }} />
                  <Link
                    to="/achievements"
                    className="block px-4 py-3 text-sm font-semibold hover:bg-[#f4a261] transition-colors"
                    style={{ 
                      color: '#3e2615',
                      fontSize: `${14 * androidTabletTextScale}px`,
                      padding: `${12 * androidTabletTextScale}px ${16 * androidTabletTextScale}px`
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Achievements
                  </Link>
                  <div className="h-px" style={{ background: '#8b6542' }} />
                  <Link
                    to="/tournaments"
                    className="block px-4 py-3 text-sm font-semibold hover:bg-[#f4a261] transition-colors"
                    style={{ 
                      color: '#3e2615',
                      fontSize: `${14 * androidTabletTextScale}px`,
                      padding: `${12 * androidTabletTextScale}px ${16 * androidTabletTextScale}px`
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Tournaments
                  </Link>
                  <div className="h-px" style={{ background: '#8b6542' }} />
                  <Link
                    to="/account"
                    className="block px-4 py-3 text-sm font-semibold hover:bg-[#f4a261] transition-colors"
                    style={{ 
                      color: '#3e2615',
                      fontSize: `${14 * androidTabletTextScale}px`,
                      padding: `${12 * androidTabletTextScale}px ${16 * androidTabletTextScale}px`
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Account
                  </Link>
                  {!adsRemovedLocal && typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform() === 'android' && (
                    <>
                      <div className="h-px" style={{ background: '#8b6542' }} />
                      <button
                        onClick={() => {
                          setIsMenuOpen(false);
                          setShowRemoveAdsModal(true);
                        }}
                        className="block w-full text-left px-4 py-3 text-sm font-semibold hover:bg-[#f4a261] transition-colors"
                        style={{ 
                          color: '#3e2615',
                          fontSize: `${14 * androidTabletTextScale}px`,
                          padding: `${12 * androidTabletTextScale}px ${16 * androidTabletTextScale}px`
                        }}
                      >
                        Remove Ads
                      </button>
                    </>
                  )}
                  <div className="h-px" style={{ background: '#8b6542' }} />
                  <Link
                    to="/contact"
                    className="block px-4 py-3 text-sm font-semibold hover:bg-[#f4a261] transition-colors"
                    style={{ 
                      color: '#3e2615',
                      fontSize: `${14 * androidTabletTextScale}px`,
                      padding: `${12 * androidTabletTextScale}px ${16 * androidTabletTextScale}px`
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Support / Help
                  </Link>
                  {onLogout && (
                    <>
                      <div className="h-px" style={{ background: '#8b6542' }} />
                      <button
                        onClick={() => {
                          setIsMenuOpen(false);
                          onLogout();
                        }}
                        className="block w-full text-left px-4 py-3 text-sm font-semibold hover:bg-[#f4a261] transition-colors"
                        style={{
                          color: '#3e2615',
                          fontSize: `${14 * androidTabletTextScale}px`,
                          padding: `${12 * androidTabletTextScale}px ${16 * androidTabletTextScale}px`
                        }}
                      >
                        Log out
                      </button>
                    </>
                  )}
                </div>
                {/* Bottom scroll indicator - triangle pointing down */}
                {showBottomIndicator && (
                  <div className="flex justify-center py-1 rounded-b-lg" style={{ borderTop: '1px solid #8b6542', background: '#fffaeb', flexShrink: 0 }}>
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 1L6 6L11 1" stroke="#8b6542" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
                </div>
              )}
            </div>
          </div>

          {/* Spacer */}
          <div className="flex-1" />
        </>
      ) : (
        <>
      {/* Left side: User info - different layout for portrait vs desktop/landscape */}
      {isPortrait ? (
        <>
          {/* Left: Avatar and Username - username at top of avatar */}
          <div className="flex items-start gap-2 flex-shrink-0">
            <Link
              to="/account"
              className="hover:opacity-80 transition-opacity"
              style={{ textDecoration: 'none' }}
            >
              <Avatar name={account.username} size={40} />
            </Link>
            <Link
              to="/account"
              className="hover:opacity-80 transition-opacity"
              style={{ textDecoration: 'none' }}
            >
              <span className="font-medium underline text-sm leading-tight block" style={{ color: '#F6E9D6' }}>
                {account.username}
              </span>
            </Link>
          </div>
          {/* Center: Glint and Gear - centered horizontally, positioned at bottom (absolute so it doesn't push hamburger off screen) */}
          <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-2 flex-nowrap" style={{ bottom: '2px' }}>
            <span className="text-sm whitespace-nowrap" style={{ color: '#F6E9D6' }}>
              <span style={{ fontSize: '16px', color: '#EAD2A3' }}>✧</span>{formatGlint(account.glint_balance)} Glint
            </span>
            <div className="w-px h-3 flex-shrink-0" style={{ background: '#3e2615' }} />
            <span className="text-sm whitespace-nowrap" style={{ color: '#F6E9D6' }}>
              <span style={{ fontSize: '16px', color: '#EAD2A3' }}>⚙️</span> {totalGear > 0 ? `Gear: ${totalGear}` : 'No Gear'}
            </span>
          </div>
        </>

      ) : isCompactLandscape ? (
        <>
          {/* Compact landscape: slide Glint/Gear under username to free horizontal space */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link
              to="/account"
              className="hover:opacity-80 transition-opacity"
              style={{ textDecoration: 'none' }}
            >
              <Avatar name={account.username} size={40} />
            </Link>
            <div className="flex flex-col leading-tight">
              <Link
                to="/account"
                className="hover:opacity-80 transition-opacity"
                style={{ textDecoration: 'none' }}
              >
                <span className="font-medium underline text-sm block" style={{ color: '#F6E9D6' }}>
                  {account.username}
                </span>
              </Link>
              <div className="flex items-center gap-2 mt-0.5 text-sm whitespace-nowrap" style={{ color: '#F6E9D6' }}>
                <span>
                  <span style={{ fontSize: '16px', color: '#EAD2A3' }}>✧</span>{formatGlint(account.glint_balance)} Glint
                </span>
                <div className="w-px h-3 flex-shrink-0" style={{ background: '#3e2615' }} />
                <span>
                  <span style={{ fontSize: '16px', color: '#EAD2A3' }}>⚙️</span> {totalGear > 0 ? `Gear: ${totalGear}` : 'No Gear'}
                </span>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Desktop/Large landscape: username left, Glint/Gear centered */}
          <div className="flex items-start gap-2 flex-shrink-0">
            <Link
              to="/account"
              className="hover:opacity-80 transition-opacity"
              style={{ textDecoration: 'none' }}
            >
              <Avatar name={account.username} size={40} />
            </Link>
            <Link
              to="/account"
              className="hover:opacity-80 transition-opacity"
              style={{ textDecoration: 'none' }}
            >
              <span className="font-medium underline leading-tight block" style={{ color: '#F6E9D6' }}>
                {account.username}
              </span>
            </Link>
          </div>
          {/* Center: Glint and Gear - centered between username and buttons, aligned with bottom of buttons */}
          <div className="flex-1 flex items-end justify-center gap-2 flex-nowrap" style={{ alignSelf: 'flex-end', paddingBottom: '0px' }}>
            <span className="whitespace-nowrap" style={{ color: '#F6E9D6' }}>
              <span style={{ fontSize: '18px', color: '#EAD2A3' }}>✧</span>{formatGlint(account.glint_balance)} Glint
            </span>
            <div className="w-px h-6 flex-shrink-0" style={{ background: '#3e2615' }} />
            <span className="whitespace-nowrap" style={{ color: '#F6E9D6' }}>
              <span style={{ fontSize: '18px', color: '#EAD2A3' }}>⚙️</span> {totalGear > 0 ? `Gear: ${totalGear}` : 'No Gear'}
            </span>
          </div>
        </>
      )}

      {/* Right side: Desktop/Landscape shows buttons, Portrait shows hamburger */}
      {isPortrait ? (
        <div className="flex items-center flex-shrink-0 ml-auto gap-0">
          {/* Bell notification - before hamburger on mobile, tucked tightly together */}
          <div className="relative" ref={notificationsRef}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setNotificationsOpen(!notificationsOpen);
              }}
              className="p-1.5 hover:opacity-80 transition-opacity relative"
              style={{ color: hasNotificationDot ? '#fee067' : '#EAD2A3' }}
              aria-label="Notifications"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {hasNotificationDot && (
                <span
                  className="absolute top-1 right-1 w-2 h-2 rounded-full"
                  style={{ background: '#fee067' }}
                />
              )}
            </button>
            {notificationsOpen && (
              <div
                className="fixed rounded-lg border-2 shadow-lg z-50"
                style={{
                  top: portraitNotificationTop,
                  right: "12px",
                  background: '#fffaeb',
                  borderColor: '#8b6542',
                  minWidth: '280px',
                  maxWidth: '320px',
                  maxHeight: 'min(70vh, 320px)',
                  overflowY: 'auto',
                  WebkitOverflowScrolling: 'touch',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {renderNotificationsContent()}
              </div>
            )}
          </div>
          <div className="relative -ml-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMenuOpen(!isMenuOpen);
              }}
              className="p-1.5 hover:opacity-80 transition-opacity"
              style={{ color: '#EAD2A3' }}
              aria-label="Menu"
            >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
          {isMenuOpen && (
            <div
              className={isIOSPad ? "fixed rounded-lg border-2 shadow-lg z-50" : "absolute right-0 top-full mt-2 rounded-lg border-2 shadow-lg z-50"}
              style={isIOSPad ? {
                background: '#fffaeb',
                borderColor: '#8b6542',
                minWidth: `${160 * landscapeMenuScale}px`,
                maxHeight: menuMaxHeight,
                display: 'flex',
                flexDirection: 'column',
                paddingTop: `${4 * landscapeMenuScale}px`,
                paddingBottom: `${4 * landscapeMenuScale}px`,
                overflow: 'hidden',
                top: 'calc(env(safe-area-inset-top, 0px) + 160px)',
                right: '16px',
              } : {
                background: '#fffaeb',
                borderColor: '#8b6542',
                minWidth: '160px',
                maxHeight: '80vh',
                display: 'flex',
                flexDirection: 'column',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Top scroll indicator - triangle pointing up */}
              {showTopIndicatorPortrait && (
                <div className="flex justify-center py-1 rounded-t-lg" style={{ borderBottom: '1px solid #8b6542', background: '#fffaeb', flexShrink: 0 }}>
                  <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 7L6 2L11 7" stroke="#8b6542" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
              <div
                ref={menuScrollRefPortrait}
                onScroll={handleMenuScrollPortrait}
                style={{ overflowY: 'auto', flex: 1 }}
              >
                <Link
                  to="/play"
                  className="block px-4 py-3 text-sm font-semibold hover:bg-[#f4a261] transition-colors"
                  style={{ color: '#3e2615', borderRadius: showTopIndicatorPortrait ? '0' : '0.5rem 0.5rem 0 0' }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Enter Mine
                </Link>
                <div className="h-px" style={{ background: '#8b6542' }} />
                <Link
                  to="/store"
                  className="block px-4 py-3 text-sm font-semibold hover:bg-[#f4a261] transition-colors"
                  style={{ color: '#3e2615' }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Store
                </Link>
                <div className="h-px" style={{ background: '#8b6542' }} />
                <Link
                  to="/leaderboard"
                  className="block px-4 py-3 text-sm font-semibold hover:bg-[#f4a261] transition-colors"
                  style={{ color: '#3e2615' }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Leaderboard
                </Link>
                <div className="h-px" style={{ background: '#8b6542' }} />
                <Link
                  to="/achievements"
                  className="block px-4 py-3 text-sm font-semibold hover:bg-[#f4a261] transition-colors"
                  style={{ color: '#3e2615' }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Achievements
                </Link>
                <div className="h-px" style={{ background: '#8b6542' }} />
                <Link
                  to="/tournaments"
                  className="block px-4 py-3 text-sm font-semibold hover:bg-[#f4a261] transition-colors"
                  style={{ color: '#3e2615' }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Tournaments
                </Link>
                <div className="h-px" style={{ background: '#8b6542' }} />
                <Link
                  to="/account"
                  className="block px-4 py-3 text-sm font-semibold hover:bg-[#f4a261] transition-colors"
                  style={{ color: '#3e2615' }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Account
                </Link>
                <div className="h-px" style={{ background: '#8b6542' }} />
                <Link
                  to="/contact"
                  className="block px-4 py-3 text-sm font-semibold hover:bg-[#f4a261] transition-colors"
                  style={{ color: '#3e2615' }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Support / Help
                </Link>
                {onLogout && (
                  <>
                    <div className="h-px" style={{ background: '#8b6542' }} />
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        onLogout();
                      }}
                      className="block w-full text-left px-4 py-3 text-sm font-semibold hover:bg-[#f4a261] transition-colors"
                      style={{ color: '#3e2615' }}
                    >
                      Log out
                    </button>
                  </>
                )}
              </div>
              {/* Bottom scroll indicator - triangle pointing down */}
              {showBottomIndicatorPortrait && (
                <div className="flex justify-center py-1 rounded-b-lg" style={{ borderTop: '1px solid #8b6542', background: '#fffaeb', flexShrink: 0 }}>
                  <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1L6 6L11 1" stroke="#8b6542" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </div>
          )}
        </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          <Link
            to="/store"
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:scale-105"
            style={{
              background: '#fffaeb',
              color: '#3e2615',
              border: '2px solid #8b6542',
            }}
          >
            Store
          </Link>
          <span style={{ color: '#3e2615' }}>|</span>
          <Link
            to="/leaderboard"
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:scale-105"
            style={{
              background: '#fffaeb',
              color: '#3e2615',
              border: '2px solid #8b6542',
            }}
          >
            Leaderboard
          </Link>
          <span style={{ color: '#3e2615' }}>|</span>
          <Link
            to="/achievements"
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:scale-105"
            style={{
              background: '#fffaeb',
              color: '#3e2615',
              border: '2px solid #8b6542',
            }}
          >
            Achievements
          </Link>
          <span style={{ color: '#3e2615' }}>|</span>
          <Link
            to="/tournaments"
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:scale-105"
            style={{
              background: '#fffaeb',
              color: '#3e2615',
              border: '2px solid #8b6542',
            }}
          >
            Tournaments
          </Link>
          {/* Bell notification - after Tournaments on desktop */}
          <div className="relative" ref={notificationsRef}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setNotificationsOpen(!notificationsOpen);
              }}
              className="p-2 hover:opacity-80 transition-opacity relative"
              style={{ color: hasNotificationDot ? '#fee067' : '#EAD2A3' }}
              aria-label="Notifications"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {hasNotificationDot && (
                <span
                  className="absolute top-1 right-1 w-2 h-2 rounded-full"
                  style={{ background: '#fee067' }}
                />
              )}
            </button>
            {notificationsOpen && (
              <div
                className="absolute right-0 top-full mt-2 rounded-lg border-2 shadow-lg z-50"
                style={{
                  background: '#fffaeb',
                  borderColor: '#8b6542',
                  minWidth: '280px',
                  maxWidth: '320px',
                  maxHeight: '260px',
                  overflowY: 'auto',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {renderNotificationsContent()}
              </div>
            )}
          </div>
        </div>
      )}
        </>
      )}
      <RemoveAdsModal
        isOpen={showRemoveAdsModal}
        onClose={() => setShowRemoveAdsModal(false)}
        onPurchaseSuccess={() => {
          window.dispatchEvent(new CustomEvent('accountNeedsRefresh'));
          setShowRemoveAdsModal(false);
        }}
      />
    </div>
  );
}
